/**
 * @fileoverview Functions and custom hooks for handling keyboard events in React components.
 * In case the rendered component is dynamic, use the function without hook.
 * In case the rendered component is static, use the hook for better performance.
 */
import { useCallback } from 'react'

// Keys that trigger 'enter' key actions
export const ENTER_KEYS = ['Enter', ' ']
// Keys that trigger 'escape' key actions
export const ESCAPE_KEYS = ['Escape']

/**
 * Returns a keydown event handler that triggers a callback when specified keys are pressed.
 * @param {string[]} keys - Array of key names to listen for (e.g., ['Enter', ' ']).
 * @param {function} callbackFn - Function to call when one of the keys is pressed.
 * @param {*} payload - Optional payload to pass to the callback (defaults to event).
 * @returns {function} - The event handler function to be attached to a keydown event.
 */
export const getOnKeyDown = (keys, callbackFn, payload) => (e) => {
  // If the pressed key is in the list and a callback is provided, trigger the callback
  if (keys.includes(e.key) && callbackFn) {
    // Use provided payload or fallback to the event object
    const _payload = payload !== undefined ? payload : e
    callbackFn(_payload)
  }
}

/**
 * Returns a keydown event handler for the Enter/Spacebar keys.
 * @param {function} callbackFn - Function to call on Enter/Spacebar keydown.
 * @param {*} payload - Optional payload for the callback.
 * @returns {function} - The event handler function.
 */
export const getOnEnterKeyDown = (callbackFn, payload) =>
  getOnKeyDown(ENTER_KEYS, callbackFn, payload)

/**
 * Returns a keydown event handler for the Escape key.
 * @param {function} callbackFn - Function to call on Escape keydown.
 * @param {*} payload - Optional payload for the callback.
 * @returns {function} - The event handler function.
 */
export const getOnEscKeyDown = (callbackFn, payload) =>
  getOnKeyDown(ESCAPE_KEYS, callbackFn, payload)

/**
 * Custom hook to handle keydown events for specified keys.
 * Memoizes the handler for performance and referential stability.
 * @param {string[]} keys - Array of key names to listen for (e.g., ['Enter', ' ']).
 * @param {function} callbackFn - Function to call when one of the keys is pressed.
 * @param {*} payload - Optional payload to pass to the callback (defaults to event).
 * @returns {function} - The event handler function to be attached to a keydown event.
 */
export const useOnKeyDown = (keys, callbackFn, payload) => {
  // Memoized handler for keydown events
  const onKeyDown = useCallback(getOnKeyDown(keys, callbackFn, payload), [
    keys,
    callbackFn,
    payload,
  ])

  return onKeyDown
}

/**
 * Custom hook for handling 'Enter' or 'Spacebar' keydown events.
 * @param {function} callbackFn - Function to call on Enter/Spacebar keydown.
 * @param {*} payload - Optional payload for the callback.
 * @returns {function} - The event handler function.
 */
export const useOnEnterKeyDown = (callbackFn, payload) =>
  useOnKeyDown(ENTER_KEYS, callbackFn, payload)

/**
 * Custom hook for handling 'Escape' keydown events.
 * @param {function} callbackFn - Function to call on Escape keydown.
 * @param {*} payload - Optional payload for the callback.
 * @returns {function} - The event handler function.
 */
export const useOnEscKeyDown = (callbackFn, payload) =>
  useOnKeyDown(ESCAPE_KEYS, callbackFn, payload)
